import type { BottomSheetHandle } from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import {
  MorphemeInlineNavigationContext,
  useMorphemePanelsHrefNavigator,
} from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { useGetMorphemePanelsSlicesQueries } from "@-/dev.improve.haniverse.lib.morpheme.read";
import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useCallback, useContext, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { GraphemePreviewSheetContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.preview-sheet";
import { GraphemePreviewSheet } from "#grapheme-preview-sheet";

/**
 * Mounts the grapheme preview sheet and hands its subtree the opener.
 *
 * Mount this on the morpheme surfaces whose mnemonic links should preview rather than navigate.
 * The mnemonic panel renders identically without it — `useGraphemeLinkPress` falls back to
 * navigation when no provider is above it.
 */
export function GraphemePreviewSheetProvider({
  children,
  morphemeHrefObject,
}: PropsWithChildren<{
  /** The host route's href prefix, used for the "Open <glyph>" navigation. */
  morphemeHrefObject: MorphemeHrefObject;
}>) {
  const sheetRef = useRef<BottomSheetHandle>(null);
  // The graphemes stepped through inside the sheet, oldest first. The last entry is on screen;
  // a link tap pushes, the back arrow pops. Kept even while the sheet is closed so the
  // "Open <glyph>" navigation can read the character after the dismissal animation starts.
  const [graphemeStack, setGraphemeStack] = useState<string[]>([]);
  const cardPageNavigator = useMorphemePanelsHrefNavigator();
  const inlineNavigate = useContext(MorphemeInlineNavigationContext);
  const queryClient = useQueryClient();
  const getMorphemePanelsSlicesQueries = useGetMorphemePanelsSlicesQueries();

  const openGraphemePreview = useCallback(
    (grapheme: string) => {
      if (inlineNavigate !== null) {
        cardPageNavigator.push.mutate({ morpheme: grapheme, prefix: morphemeHrefObject });
        return;
      }
      // Warm the two slices the preview panel suspends on. Without this the panel mounts against
      // a cold cache on every first look at a character, which flashes the spinner and trips
      // `usePrefetchedSuspenseQueries`'s "queries suspended" warning.
      const {
        mnemonicsPanelMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeUserSliceQueryOptions,
      } = getMorphemePanelsSlicesQueries(grapheme);
      void queryClient.prefetchQuery(mnemonicsPanelMorphemeSliceQueryOptions);
      void queryClient.prefetchQuery(mnemonicsPanelMorphemeUserSliceQueryOptions);

      setGraphemeStack((previousStack) =>
        // Re-opening the sheet starts a new stack; a tap while it is open goes deeper. An empty
        // stack means the sheet is closed, so `open()` below is a no-op when it is already up.
        hasLength(previousStack, 0) ? [grapheme] : [...previousStack, grapheme],
      );
      sheetRef.current?.open();
    },
    [
      cardPageNavigator,
      getMorphemePanelsSlicesQueries,
      inlineNavigate,
      morphemeHrefObject,
      queryClient,
    ],
  );

  const handleBack = useCallback(() => {
    setGraphemeStack((previousStack) =>
      arrayLength(previousStack) <= 1 ? previousStack : previousStack.slice(0, -1),
    );
  }, []);

  // The stack is per-presentation: a dismissed sheet must reopen on whatever link is tapped next,
  // not resume mid-stack behind a stale back arrow.
  const handleDismiss = useCallback(() => {
    setGraphemeStack([]);
  }, []);

  const handleOpenGrapheme = useCallback(
    (grapheme: string) => {
      // Leave the stack alone: `close()` only *starts* the dismiss animation, and clearing it here
      // would blank the sheet body mid-slide. `handleDismiss` resets it once the sheet is gone.
      sheetRef.current?.close();
      cardPageNavigator.push.mutate({ morpheme: grapheme, prefix: morphemeHrefObject });
    },
    [cardPageNavigator, morphemeHrefObject],
  );

  const value = useMemo(() => ({ openGraphemePreview }), [openGraphemePreview]);
  const activeGrapheme = graphemeStack.at(-1) ?? null;

  return (
    <GraphemePreviewSheetContext.Provider value={value}>
      {children}
      <GraphemePreviewSheet
        canGoBack={arrayLength(graphemeStack) > 1}
        grapheme={activeGrapheme}
        morphemeHrefObject={morphemeHrefObject}
        onBack={handleBack}
        onDismiss={handleDismiss}
        onOpenGrapheme={handleOpenGrapheme}
        ref={sheetRef}
      />
    </GraphemePreviewSheetContext.Provider>
  );
}
